import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import cn from "classnames";
import BrickwallDonationForm from "./BrickwallDonationForm";

// Function to determine if a color is dark
const isDarkColor = (color) => {
	if (!color) return false;

	try {
		// Remove # if present and trim whitespace
		let hex = color.replace("#", "").trim();

		// Handle 3-character hex codes (e.g., #fff -> #ffffff)
		if (hex.length === 3) {
			hex = hex
				.split("")
				.map((char) => char + char)
				.join("");
		}

		// Validate hex code length
		if (hex.length !== 6) return false;

		// Convert hex to RGB
		const r = parseInt(hex.substring(0, 2), 16);
		const g = parseInt(hex.substring(2, 4), 16);
		const b = parseInt(hex.substring(4, 6), 16);

		// Check if parsing was successful
		if (isNaN(r) || isNaN(g) || isNaN(b)) return false;

		// Calculate relative luminance using the formula from WCAG
		// https://www.w3.org/WAI/GL/wiki/Relative_luminance
		const getLuminance = (val) => {
			val = val / 255;
			return val <= 0.03928
				? val / 12.92
				: Math.pow((val + 0.055) / 1.055, 2.4);
		};

		const luminance =
			0.2126 * getLuminance(r) +
			0.7152 * getLuminance(g) +
			0.0722 * getLuminance(b);

		// Consider color dark if luminance is less than 0.5
		return luminance < 0.5;
	} catch (error) {
		// If anything goes wrong, default to not dark
		return false;
	}
};

const BrickwallBrick = ({
	row,
	col,
	donation,
	isSelected,
	isHovered,
	isAvailable,
	onMouseDown,
	onMouseEnter,
	brickPrice,
	selectedAmount,
	donorName,
	brickColor,
	hideNameOnBrick,
	hideAmountOnBrick,
	isDisplay = false,
	config,
	register,
	errors,
	setValue,
	watch,
	trigger,
	onDonate,
	onClearSelection,
}) => {
	const isOccupied = !!donation;
	const brickKey = `${row},${col}`;

	// Format date for display
	const formatDate = (dateString) => {
		if (!dateString) return "";
		const date = new Date(dateString);
		return date.toLocaleDateString("en-GB", {
			day: "2-digit",
			month: "2-digit",
			year: "2-digit",
		});
	};

	// Calculate total donation amount for this donation group
	const amount = donation?.amount ?? brickPrice;

	// Simple amount to show when this brick is currently selected
	const selectedDisplayAmount = (() => {
		if (typeof selectedAmount === "number") return selectedAmount;
		const num = parseFloat(selectedAmount);
		return !isNaN(num) && num > 0 ? num : brickPrice;
	})();

	// Derive hover popup content (only show when there is something meaningful)
	const hideName = donation?.hide_name == "true";
	const hideAmount = donation?.hide_amount == "true";

	if (donation) console.log("donation", donation);

	const hoveredName =
		isOccupied && donation?.donor
			? hideName
				? "Anonymous"
				: donation.donor
			: isSelected && donorName
				? hideNameOnBrick
					? "Anonymous"
					: donorName
				: null;

	const hoveredAmount =
		isOccupied && !hideAmount
			? amount
			: isSelected && !hideAmountOnBrick
				? selectedDisplayAmount
				: null;

	const hoveredMessage = donation?.donation_message || null;

	const hoveredImage = donation?.donation_image || null;

	const hasHoverContent =
		hoveredName || hoveredAmount || hoveredMessage || hoveredImage;

	// Track mouse position for tooltip
	const [mousePosition, setMousePosition] = useState(null);

	// Track brick position for form tooltip
	const brickRef = useRef(null);
	const [brickPosition, setBrickPosition] = useState(null);

	// Update brick position when selected, and on scroll/resize
	const updateBrickPosition = () => {
		if (isSelected && !isDisplay && brickRef.current) {
			const rect = brickRef.current.getBoundingClientRect();
			setBrickPosition({
				x: rect.left + rect.width / 2,
				y: rect.top,
				bottom: rect.bottom,
				height: rect.height,
			});
		}
	};

	useEffect(() => {
		if (isSelected && !isDisplay) {
			// Initial position update
			updateBrickPosition();

			// Update position on scroll and resize
			window.addEventListener("scroll", updateBrickPosition, true);
			window.addEventListener("resize", updateBrickPosition);

			return () => {
				window.removeEventListener("scroll", updateBrickPosition, true);
				window.removeEventListener("resize", updateBrickPosition);
			};
		} else {
			setBrickPosition(null);
		}
	}, [isSelected, isDisplay]);

	// Get tooltip position exactly at cursor with viewport constraints
	const getTooltipPosition = () => {
		if (!mousePosition) return null;

		const offset = 8; // Small offset so tooltip doesn't cover cursor
		const estimatedWidth = 250; // estimated tooltip width
		const estimatedHeight = 150; // estimated tooltip height
		const viewportWidth = window.innerWidth;
		const viewportHeight = window.innerHeight;

		// Position tooltip exactly at cursor position
		let tooltipX = mousePosition.x + offset;
		let tooltipY = mousePosition.y + offset;

		// Keep vertical position within viewport bounds
		if (tooltipY + estimatedHeight > viewportHeight) {
			tooltipY = viewportHeight - estimatedHeight - offset;
		}

		if (tooltipY < 0) {
			tooltipY = offset;
		}

		// Ensure horizontal position stays within bounds
		if (tooltipX < offset) {
			tooltipX = offset;
		}

		if (tooltipX + estimatedWidth > viewportWidth - offset) {
			tooltipX = viewportWidth - estimatedWidth - offset;
		}

		return {
			x: tooltipX,
			y: tooltipY,
		};
	};

	const handleMouseEnterBrick = () => {
		onMouseEnter(row, col);
	};

	const handleMouseMove = (e) => {
		if (!hasHoverContent || isDisplay) {
			if (mousePosition !== null) {
				setMousePosition(null);
			}
			return;
		}

		setMousePosition({
			x: e.clientX,
			y: e.clientY,
		});
	};

	const handleMouseLeaveBrick = () => {
		setMousePosition(null);
	};

	// Determine the state: occupied, empty, can-place, selected, or unavailable
	const getState = () => {
		if (isOccupied) return "occupied";
		if (isSelected) return "selected";
		if (isAvailable) return "can-place";
		return "unavailable";
	};

	const state = getState();

	// Use selected color if brick is selected, otherwise use donation color or default
	const displayColor = isSelected ? brickColor : donation?.color;

	// Check if the background color is dark
	const isDark = isDarkColor(displayColor);

	// Text color style - white if dark background, otherwise use #00288C for light backgrounds
	const textColorStyle = isDark ? { color: "#fff" } : { color: "#00288C" };

	// if (donation) debugger;
	return (
		<>
			<div className="brickwall-brick">
				<div
					ref={brickRef}
					className={cn("brickwall-brick-inner", {
						"brickwall-brick-inner--occupied":
							state === "occupied" && !isDisplay,
						"brickwall-brick-inner--selected":
							state === "selected" && !isDisplay,
						"brickwall-brick-inner--can-place":
							state === "can-place" && !isDisplay,
						"brickwall-brick-inner--unavailable":
							state === "unavailable" && !isDisplay,
						"brickwall-brick-inner--display": isDisplay,
					})}
					onMouseDown={isDisplay ? undefined : (e) => onMouseDown(row, col, e)}
					onMouseEnter={isDisplay ? undefined : handleMouseEnterBrick}
					onMouseMove={isDisplay ? undefined : handleMouseMove}
					onMouseLeave={isDisplay ? undefined : handleMouseLeaveBrick}
					style={
						isDisplay
							? undefined
							: {
									position: "relative",
									backgroundColor: displayColor,
								}
					}
				>
					{isDisplay ? null : isOccupied ? (
						<div className="brickwall-brick-content" style={textColorStyle}>
							{!hideAmount && (
								<div className="brickwall-brick-amount" style={textColorStyle}>
									£{amount.toFixed(2)}
								</div>
							)}
							{donation.donor && (
								<div className="brickwall-brick-donor" style={textColorStyle}>
									{hideName ? "Anonymous" : donation.donor}
								</div>
							)}
							<div className="brickwall-brick-date" style={textColorStyle}>
								{donation.date || formatDate(new Date())}
							</div>
						</div>
					) : isSelected ? (
						<div className="brickwall-brick-content" style={textColorStyle}>
							{!hideAmountOnBrick && (
								<div className="brickwall-brick-amount" style={textColorStyle}>
									£{selectedDisplayAmount.toFixed(2)}
								</div>
							)}
							{donorName && (
								<div className="brickwall-brick-donor" style={textColorStyle}>
									{hideNameOnBrick ? "Anonymous" : donorName}
								</div>
							)}
						</div>
					) : isAvailable ? (
						<div className="brickwall-brick-empty">
							<div className="brickwall-brick-increment">+ Add Brick</div>
						</div>
					) : (
						<div className="brickwall-brick-disabled"></div>
					)}
					{mousePosition &&
						hasHoverContent &&
						!isDisplay &&
						!isSelected &&
						typeof document !== "undefined" &&
						(() => {
							const tooltipPos = getTooltipPosition();
							return (
								tooltipPos &&
								ReactDOM.createPortal(
									<div
										className="brickwall-brick-tooltip"
										style={{
											position: "fixed",
											top: tooltipPos.y,
											left: tooltipPos.x,
											zIndex: 9999,
											pointerEvents: "none",
										}}
									>
										{hoveredName && (
											<div className="brickwall-brick-tooltip-name">
												Donated by {hoveredName}
											</div>
										)}
										{typeof hoveredAmount === "number" && (
											<div className="brickwall-brick-tooltip-amount">
												£{hoveredAmount.toFixed(2)}
											</div>
										)}
										{hoveredMessage && (
											<div className="brickwall-brick-tooltip-message">
												{hoveredMessage}
											</div>
										)}
										{hoveredImage && (
											<div className="brickwall-brick-tooltip-image">
												<img src={hoveredImage} alt="" />
											</div>
										)}
									</div>,
									document.body,
								)
							);
						})()}
				</div>
			</div>

			{/* Show donation form as tooltip when brick is selected */}
			{isSelected && !isDisplay && brickPosition && (
				<BrickwallDonationForm
					config={config}
					register={register}
					errors={errors}
					setValue={setValue}
					watch={watch}
					trigger={trigger}
					onDonate={onDonate}
					onClearSelection={onClearSelection}
					brickPosition={brickPosition}
				/>
			)}
		</>
	);
};

export default BrickwallBrick;
